import {inject} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree} from '@angular/router';
import {AppContext} from '../app-context';
import {sendQuery} from '../common/utils';

const OLD_PATH = 'commercial-release';
const NEW_PATH = 'commercial-release-new';

// NOTE on a fixed deadlock: this guard used to only *wait* for AppContext.userProfile,
// on the assumption that AppComponent.routeBasedOnUserProfile (app.component.ts) would
// populate it "on the same navigation". That's circular for a canActivate guard: Angular
// does not fire the router's NavigationEnd event - the only thing that triggers
// routeBasedOnUserProfile - until every canActivate guard for the activating route,
// including this one, has already resolved. So on any fresh/first navigation (bookmark,
// direct link, hard refresh) nothing could ever set the field this guard was polling,
// and it always exhausted its wait and fell through to `return true` (no redirect) -
// exactly the scenario this guard exists to fix. Confirmed live by two verifiers.
//
// Fix: if the profile isn't already cached, this guard fetches it itself via the same
// GetUserProfile query routeBasedOnUserProfile uses, and caches the result onto
// AppContext.userProfile ONLY if it's still unset by the time the response arrives -
// never overwriting a value routeBasedOnUserProfile (or a previous run of this guard)
// may have already assigned, so the two call sites can't race or clobber each other.
// This guard does not replicate routeBasedOnUserProfile's other side effects (Beamer
// init, Sentry scope, waste-collector/systemlink redirects, the cargo look-and-feel
// auto-switch) - those remain solely routeBasedOnUserProfile's responsibility and still
// run normally once NavigationEnd fires right after this guard resolves.
function resolveUserProfile(): Promise<void> {
  if (AppContext.userProfile) {
    return Promise.resolve();
  }
  return new Promise(resolve => {
    sendQuery('com.portbase.bezoekschip.common.api.authorisation.GetUserProfile', {}, {caching: false}).subscribe({
      next: profile => {
        if (!AppContext.userProfile) {
          AppContext.userProfile = profile;
        }
        resolve();
      },
      error: () => resolve() // let the guard's own !AppContext.userProfile fallback (render as-is) handle failure.
    });
  });
}

/**
 * Redirects between the OLD (/commercial-release) and NEW (/commercial-release-new)
 * commercial release screens when the URL a user landed on (bookmark, direct link,
 * stale link, hard refresh) does not match the screen their organisation is actually
 * entitled to per AppContext.commercialReleaseNewScreenEnabled() - the exact same
 * formula the nav tab uses, so the guard and the nav tab can never disagree.
 *
 * This is a UX/routing convenience only, not a security/authorization boundary - it
 * does not gate access based on role.
 *
 * CRN preservation is intentionally asymmetric (see task
 * commercial-release-old-new-screen-redirect-guard.md, "Decision: CRN preservation"):
 * - new -> old: the CRN is always dropped. The OLD screen's component never reads a
 *   :crn route param today, so preserving it would invent behavior the old screen
 *   doesn't support.
 * - old -> new: the CRN is preserved ONLY when redirecting from the nested
 *   details/:crn/commercial-release variant (ancestor :crn param), since the NEW
 *   screen's /commercial-release-new/:crn route meaningfully uses it. Redirecting
 *   from the top-level /commercial-release (no CRN in scope) targets the bare
 *   /commercial-release-new.
 * Do not "fix" this into false symmetry - it reflects what each screen actually reads.
 */
export const commercialReleaseRedirectGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot
): Promise<boolean | UrlTree> => {
  const router = inject(Router);

  await resolveUserProfile();

  if (!AppContext.userProfile) {
    // The fetch failed/errored (e.g. not authenticated yet). The existing
    // 401-redirect-to-login / unavailability-alert path in routeBasedOnUserProfile
    // already handles that failure mode elsewhere once NavigationEnd fires - render
    // the requested route rather than introducing a second, conflicting failure path.
    return true;
  }

  const newScreenEnabled = AppContext.commercialReleaseNewScreenEnabled();
  const isOnNewRoute = route.routeConfig?.path?.startsWith(NEW_PATH) ?? false;

  if (newScreenEnabled === isOnNewRoute) {
    return true;
  }

  if (newScreenEnabled) {
    // old -> new
    const crn = route.parent?.params['crn'];
    return crn
      ? router.createUrlTree(['/' + NEW_PATH, crn])
      : router.createUrlTree(['/' + NEW_PATH]);
  }

  // new -> old: CRN is always dropped, the old screen doesn't read it.
  return router.createUrlTree(['/' + OLD_PATH]);
};
